/**
 * Leitura e escrita binária sobre `DataView`. Funciona sem alteração no Node e no
 * navegador — `Buffer` é um `Uint8Array`, então o mesmo código serve aos dois lados.
 *
 * Tudo é little-endian por decisão do protocolo (PRD §4); os métodos já passam
 * `true` para `DataView`, então nenhum chamador precisa lembrar disso.
 */
/**
 * Instâncias únicas: `TextEncoder`/`TextDecoder` são caros de construir e não guardam
 * estado entre chamadas, então criar um por pacote seria alocação pura no caminho quente.
 */
const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();
/** Erro de decodificação. Frame malformado é sempre motivo para derrubar a conexão. */
export class ProtocolError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ProtocolError';
    }
}
export class BinaryWriter {
    /** Guardado explicitamente porque `Uint8Array.buffer` é tipado como `ArrayBufferLike`. */
    buffer;
    view;
    bytes;
    pos = 0;
    constructor(initialCapacity = 256) {
        this.buffer = new ArrayBuffer(initialCapacity);
        this.view = new DataView(this.buffer);
        this.bytes = new Uint8Array(this.buffer);
    }
    /** Bytes escritos até agora. */
    get length() {
        return this.pos;
    }
    /**
     * Zera a posição sem soltar o buffer, para reaproveitar o mesmo writer entre ticks.
     * É o que sustenta a regra de zero alocação no caminho quente (PRD §3.1).
     */
    reset() {
        this.pos = 0;
        return this;
    }
    ensure(extra) {
        const needed = this.pos + extra;
        if (needed <= this.bytes.length)
            return;
        let capacity = this.bytes.length * 2;
        while (capacity < needed)
            capacity *= 2;
        const grown = new ArrayBuffer(capacity);
        const grownBytes = new Uint8Array(grown);
        grownBytes.set(this.bytes);
        this.buffer = grown;
        this.bytes = grownBytes;
        this.view = new DataView(grown);
    }
    u8(value) {
        this.ensure(1);
        this.view.setUint8(this.pos, value);
        this.pos += 1;
        return this;
    }
    u16(value) {
        this.ensure(2);
        this.view.setUint16(this.pos, value, true);
        this.pos += 2;
        return this;
    }
    u32(value) {
        this.ensure(4);
        this.view.setUint32(this.pos, value, true);
        this.pos += 4;
        return this;
    }
    i16(value) {
        this.ensure(2);
        this.view.setInt16(this.pos, value, true);
        this.pos += 2;
        return this;
    }
    f32(value) {
        this.ensure(4);
        this.view.setFloat32(this.pos, value, true);
        this.pos += 4;
        return this;
    }
    /**
     * Usado para experiência. Um u32 estouraria em 4,29 bilhões — alcançável em um jogo
     * de progressão sem limite (PRD §1) — e o estouro seria silencioso: a barra de
     * experiência simplesmente voltaria ao começo. `f64` é exato até 2^53.
     */
    f64(value) {
        this.ensure(8);
        this.view.setFloat64(this.pos, value, true);
        this.pos += 8;
        return this;
    }
    /**
     * Texto curto: 1 byte de tamanho + UTF-8. Cobre nome de personagem e linha de chat.
     *
     * O corte é em **bytes**, não em caracteres, e é feito byte a byte para trás até
     * cair numa fronteira de code point: cortar UTF-8 no meio de um caractere produziria
     * bytes inválidos que o `TextDecoder` do outro lado transformaria em "�".
     */
    str8(value) {
        let bytes = TEXT_ENCODER.encode(value);
        if (bytes.length > 255) {
            let end = 255;
            while (end > 0 && (bytes[end] & 0xc0) === 0x80)
                end--;
            bytes = bytes.subarray(0, end);
        }
        this.u8(bytes.length);
        this.raw(bytes);
        return this;
    }
    raw(source) {
        this.ensure(source.length);
        this.bytes.set(source, this.pos);
        this.pos += source.length;
        return this;
    }
    /**
     * Vista sobre os bytes escritos, **sem cópia**. É o que se passa para `ws.send()`.
     *
     * A vista compartilha memória com o writer: qualquer escrita seguinte (ou `reset()`
     * mais escrita) a corrompe. Use e descarte antes de tocar no writer de novo.
     */
    toUint8() {
        return this.bytes.subarray(0, this.pos);
    }
    /**
     * Cópia independente dos bytes escritos. Use quando o buffer precisa sobreviver ao
     * writer — por exemplo para transferir por `MessagePort` na transfer list.
     */
    toArrayBuffer() {
        return this.buffer.slice(0, this.pos);
    }
}
export class BinaryReader {
    view;
    bytes;
    pos = 0;
    constructor(source) {
        this.bytes =
            source instanceof Uint8Array ? source : new Uint8Array(source);
        this.view = new DataView(this.bytes.buffer, this.bytes.byteOffset, this.bytes.byteLength);
    }
    get offset() {
        return this.pos;
    }
    get remaining() {
        return this.bytes.byteLength - this.pos;
    }
    need(count) {
        if (this.remaining < count) {
            throw new ProtocolError(`frame truncado: pedia ${count} byte(s) no offset ${this.pos}, restavam ${this.remaining}`);
        }
    }
    u8() {
        this.need(1);
        const value = this.view.getUint8(this.pos);
        this.pos += 1;
        return value;
    }
    u16() {
        this.need(2);
        const value = this.view.getUint16(this.pos, true);
        this.pos += 2;
        return value;
    }
    u32() {
        this.need(4);
        const value = this.view.getUint32(this.pos, true);
        this.pos += 4;
        return value;
    }
    i16() {
        this.need(2);
        const value = this.view.getInt16(this.pos, true);
        this.pos += 2;
        return value;
    }
    f32() {
        this.need(4);
        const value = this.view.getFloat32(this.pos, true);
        this.pos += 4;
        return value;
    }
    f64() {
        this.need(8);
        const value = this.view.getFloat64(this.pos, true);
        this.pos += 8;
        return value;
    }
    /** Contraparte de `BinaryWriter.str8`. */
    str8() {
        const length = this.u8();
        this.need(length);
        const text = TEXT_DECODER.decode(this.bytes.subarray(this.pos, this.pos + length));
        this.pos += length;
        return text;
    }
    /** Cópia de `count` bytes. Copia de propósito: o dono do frame original pode reciclá-lo. */
    raw(count) {
        this.need(count);
        const slice = this.bytes.slice(this.pos, this.pos + count);
        this.pos += count;
        return slice;
    }
}
//# sourceMappingURL=binary.js.map